import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { ajaxGetPurchaseOrdersHistoryByPo } from '../../services/purchaseOrderHistoryService';
import { ajaxGetPurchaseOrders } from '../../services/purchaseOrderService';
import { toDateText, toDateTimeText, priceFormat } from '../../helpers';
import useWorkspace from '../../hooks/useWorkspace';
import LoadingSpinner from '../../components/LoadingSpinner';
import ViewDownloadIconButton from '../../components/ViewDownloadIconButton';
import Page from '../../components/Page';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import EnhancedTable from '../../components/EnhancedTable'
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';

const dateFormat = (v) => v ? toDateText(v) : '-';
const dateTimeFormat = (v) => v ? toDateTimeText(v) : '-';

export default function PurchaseOrderHistoryList() {
  const { PurchaseOrderId } = useParams();
  const [workspace, setWorkspace] = useWorkspace(true, 'purchaseOrderHistoryPage', { table: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [purchaseOrder, setPurchaseOrder] = useState(null);

  const navigate = useNavigate();

  const onTableParams = useCallback((params) => {
    if ( isLoading ) {
      return;
    }
    const {pageItems, ...restParams} = params;
    setWorkspace(prev => ({
      ...prev,
      table: {
        ...prev.table,
        ...restParams
      }
    }));
  }, [isLoading, setWorkspace]);

  useEffect(() => {
    ajaxGetPurchaseOrders({ PurchaseOrderId })
      .then((res) => {
        setPurchaseOrder(res.data);
        return ajaxGetPurchaseOrdersHistoryByPo({ PurchaseOrderId });
      })
      .then((res) => {
        setItems(res.data);
        setIsLoading(false);
      })
      .catch(() => {
        setItems([]);
        setIsLoading(false);
      });
  }, [PurchaseOrderId]);

  const columns = [
    {
      id: 'date',
      label: 'Date',
      minWidth: 100,
      format: dateTimeFormat,
      sortable: true
    },
    {
      id: 'po_date',
      label: 'PO Date',
      minWidth: 100,
      format: dateFormat,
      sortable: true
    },
    {
      id: 'po_number',
      label: 'PO Number',
      minWidth: 100,
      sortable: true
    },
    {
      id: 'supplier_name',
      label: 'Supplier',
      sortable: true
    },
    {
      id: 'requisitioner',
      label: 'Requisitioner',
      sortable: true
    },
    {
      id: 'purpose',
      label: 'Purpose',
      sortable: true
    },
    {
      id: 'department',
      label: 'Department',
      sortable: true
    },
    {
      id: 'due_date',
      label: 'Due Date',
      minWidth: 100,
      format: dateFormat,
      sortable: true
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      sortable: true,
      format: (value, row) =>
        <div style={{ textAlign: 'center' }}>
          {value === 'cancelled' ? (
            <Tooltip
              title={
                <React.Fragment>
                  <div style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
                    {row.cancelled_reason || 'No reason provided'}
                  </div>
                </React.Fragment>
              }
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    bgcolor: 'rgba(0, 0, 0, 0.9)',
                    '& .MuiTooltip-arrow': {
                      color: 'rgba(0, 0, 0, 0.9)',
                    },
                  },
                },
              }}
            >
              <Chip
                sx={{color: value === 'draft' ? '#000' : '#fff', height: '18px', fontSize: '12px'}}
                label={
                  value === 'draft' ? 'Draft' :
                  value === 'waitingapproval' ? 'Waiting Approval' :
                  value === 'approved' ? 'Approved' :
                  value === 'rejected' ? 'Rejected' :
                  value === 'received' ? 'Received' :
                  value === 'cancelled' ? 'Cancelled' :
                  'Unknown'
                }
                color={
                  value === 'draft' ? 'default' :
                  value === 'waitingapproval' ? 'warning' :
                  value === 'approved' ? 'success' :
                  value === 'rejected' ? 'error' :
                  value === 'received' ? 'info' :
                  value === 'cancelled' ? 'error' :
                  'default'
                }
                size="small"
              />
            </Tooltip>
          ) : (
            <Chip
              sx={{color: value === 'draft' ? '#000' : '#fff', height: '18px', fontSize: '12px'}}
              label={
                value === 'draft' ? 'Draft' :
                value === 'waitingapproval' ? 'Waiting Approval' :
                value === 'approved' ? 'Approved' :
                value === 'rejected' ? 'Rejected' :
                value === 'received' ? 'Received' :
                value === 'cancelled' ? 'Cancelled' :
                'Unknown'
              }
              color={
                value === 'draft' ? 'default' :
                value === 'waitingapproval' ? 'warning' :
                value === 'approved' ? 'success' :
                value === 'rejected' ? 'error' :
                value === 'received' ? 'info' :
                value === 'cancelled' ? 'error' :
                'default'
              }
              size="small"
            />
          )}
        </div>
    },
    {
      id: 'amount',
      label: 'Amount',
      minWidth: 100,
      sortable: true,
      format: (value) => priceFormat(value, 2, 6),
      summary: (item, column) => item[column.id] || 0
    },
    {
      id: 'paid',
      label: 'Paid',
      minWidth: 100,
      sortable: true,
      format: (value) =>
        value === 1 ?
          <div style={{ textAlign: 'center' }}>
            <Chip
              sx={{color: '#fff', height: '18px', fontSize: '12px'}}
              label={'Paid'}
              color={'info'}
              size="small"
            />
          </div>
        :
        <div />
    },
    {
      id: 'proof_payment_pdf',
      label: 'Proof of Payment',
      minWidth: 100,
      format: (value, row) => (
        row.paid === 1 ?
        <div style={{ textAlign: 'center' }}>
          <ViewDownloadIconButton
            id={row.id}
            fileType="purchase_order_history/proof_payment_pdf"
            fileName={row.po_number + '-proof-payment'}
            fileExt="pdf"
          />
        </div>
        :
        <div />
      )
    },
    {
      id: 'supplier_quote_pdf',
      label: 'Supplier Quote',
      minWidth: 100,
      format: (value, row) => (
        row.supplier_quote_pdf ?
        <div style={{ textAlign: 'center' }}>
          <ViewDownloadIconButton
            id={row.id}
            fileType="purchase_order_history/supplier_quote_pdf"
            fileName={row.po_number + '-supplier-quote'}
            fileExt="pdf"
          />
        </div>
        :
        <div />
      )
    },
    {
      id: 'actions',
      label: 'PO PDF',
      minWidth: 150,
      format: (value, row) => (
        row.status === 'approved' || row.status === 'received' ?
        <div style={{ textAlign: 'center' }}>
          <ViewDownloadIconButton
            id={row.id}
            fileType="purchase_order_history/po_pdf"
            fileName={row.po_number}
            fileExt="pdf"
          />
        </div>
        :
        <div />
      )
    }
  ];

  return (
    <Page
      title={
        [
          'Purchase Order History',
          purchaseOrder?.po_number
        ]
      }
      maxWidth={false}
      sx={{ display: 'flex', flexDirection: 'column' }}
      backButton={
        <IconButton
          color="primary"
          component={RouterLink}
          to={`/purchaseorders/edit/${PurchaseOrderId}`}
        >
          <ArrowBackIcon fontSize="medium" />
        </IconButton>
      }
    >
      {
        isLoading ?
        <LoadingSpinner />
        :
        <React.Fragment>
          <Box sx={{ flexGrow: 1, position: 'relative' }}>
            <Box sx={{ position: 'absolute', inset: '0px 0px 10px 0px' }}>
              <EnhancedTable
                border
                fullHeight
                stickyColumn
                fontSize="12px"
                columns={columns}
                items={items}
                onRowClick={(id) => navigate(`/purchaseorders/history/${PurchaseOrderId}/${id}`)}
                order={workspace.table.order || 'desc'}
                orderBy={workspace.table.orderBy || 'date'}
                page={workspace.table.pageIndex || 0}
                rowsPerPage={workspace.table.rowsPerPage || 50}
                onTableParams={onTableParams}
              />
            </Box>
          </Box>
        </React.Fragment>
      }
    </Page>
  );
} 